import { Stethoscope } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { NumField, SelectField, TextField } from './ClinicalFieldControls';
import { SectionCard } from './SectionCard';

interface PhysicalExamSectionProps {
  exam: Record<string, string>;
  editable: boolean;
  onChange: (key: string, value: string) => void;
  onBlurSave: () => void;
}

// Enum value lists live API-side only (`clinical.enums.ts`) — mirrored here as local const
// arrays, same pattern as `DIAGNOSIS_KINDS` in DiagnosesSection.tsx, since web can't import
// across the app boundary. Labels come from the existing `clinical.<enum>_<value>` i18n keys.
// MM (mucous_membranes) is the only EFG field left with a fixed option list — E. Mental
// (consciousness_level) and DH (hydration_status) moved to open free text (clinic request,
// 2026-07-10): the canned options were too restrictive for real dictation/typing.
const MUCOUS_VALUES = ['normal', 'pale', 'icteric', 'cyanotic', 'hyperemic', 'dry', 'tacky'] as const;

type EfgCell =
  | { kind: 'select'; key: string; values: readonly string[]; i18nPrefix: string }
  | { kind: 'text'; key: string }
  | { kind: 'num'; key: string };

// Canonical 4x4 EFG order, confirmed against clinical-record-print.ts's buildEfgHtml grid.
const EFG_CELLS: EfgCell[] = [
  { kind: 'text', key: 'consciousness_level' },
  { kind: 'num', key: 'body_condition_score' },
  { kind: 'text', key: 'hydration_status' },
  { kind: 'text', key: 'cardiovascular_notes' },

  { kind: 'select', key: 'mucous_membranes', values: MUCOUS_VALUES, i18nPrefix: 'mucous_membranes' },
  { kind: 'text', key: 'capillary_refill_time' },
  { kind: 'text', key: 'cough_reflex' },
  { kind: 'text', key: 'gag_reflex' },

  { kind: 'num', key: 'heart_rate_bpm' },
  { kind: 'text', key: 'pulse_description' },
  { kind: 'num', key: 'respiratory_rate_bpm' },
  { kind: 'text', key: 'abdomen_notes' },

  { kind: 'text', key: 'lymph_node_notes' },
  { kind: 'text', key: 'auscultation_percussion_notes' },
  { kind: 'text', key: 'respiratory_notes' },
  { kind: 'num', key: 'body_temperature_celsius_tenths' },
];

export function PhysicalExamSection({
  exam,
  editable,
  onChange,
  onBlurSave,
}: PhysicalExamSectionProps): JSX.Element {
  const { t } = useTranslation();
  const blur = editable ? onBlurSave : undefined;

  return (
    <div className="space-y-3">
      <SectionCard icon={Stethoscope} title={t('clinical.tab_exam')}>
        <NumField
          label={t('clinical.f_body_weight_grams')}
          value={exam.body_weight_grams ?? ''}
          disabled={!editable}
          onChange={(v) => onChange('body_weight_grams', v)}
          onBlur={blur}
        />

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {EFG_CELLS.map((cell) => {
            const label = t(`clinical.f_${cell.key}`);
            const placeholder = t(`clinical.f_${cell.key}_full`);
            if (cell.kind === 'select') {
              return (
                <SelectField
                  key={cell.key}
                  label={label}
                  value={exam[cell.key] ?? ''}
                  disabled={!editable}
                  onChange={(v) => onChange(cell.key, v)}
                  onBlur={blur}
                  placeholder={placeholder}
                  options={cell.values.map((v) => ({
                    value: v,
                    label: t(`clinical.${cell.i18nPrefix}_${v}`),
                  }))}
                />
              );
            }
            if (cell.kind === 'num') {
              return (
                <NumField
                  key={cell.key}
                  label={label}
                  value={exam[cell.key] ?? ''}
                  disabled={!editable}
                  onChange={(v) => onChange(cell.key, v)}
                  onBlur={blur}
                  placeholder={placeholder}
                />
              );
            }
            return (
              <TextField
                key={cell.key}
                label={label}
                value={exam[cell.key] ?? ''}
                disabled={!editable}
                onChange={(v) => onChange(cell.key, v)}
                onBlur={blur}
                placeholder={placeholder}
              />
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}
